/**
 * Fehlermeldungen aus Supabase-Edge-Function-Aufrufen lesbar machen.
 *
 * `supabase.functions.invoke` verpackt jede Nicht-2xx-Antwort in einen
 * generischen `FunctionsHttpError` ("Edge Function returned a non-2xx status
 * code") und lässt `data` auf null — die eigentliche Server-Meldung steckt im
 * Response-Body (`error.context`). Diese Helfer holen sie hervor, damit der
 * Nutzer den echten Grund sieht statt der nichtssagenden Standardmeldung.
 */

/**
 * Zieht die spezifische Server-Fehlermeldung aus einem FunctionsHttpError
 * (dessen `context` ein Response-Objekt ist), sonst den Fallback.
 */
export async function extractFunctionError(error: unknown, fallback: string): Promise<string> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    try {
      const body = (await ctx.clone().json()) as { error?: unknown; message?: unknown };
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
      if (typeof body.message === 'string' && body.message.trim()) return body.message;
    } catch {
      // Body nicht lesbar / kein JSON → Fallback.
    }
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

/**
 * Wertet das Ergebnis eines `functions.invoke`-Aufrufs aus: wirft mit der echten
 * Server-Meldung bei Transport-Fehlern (non-2xx) oder bei `{ success: false }`,
 * und liefert sonst die typisierten Daten zurück.
 */
export async function unwrapFunctionResult<T extends { success?: boolean; error?: string }>(
  result: { data: T | null; error: unknown },
  fallback: string
): Promise<T> {
  const { data, error } = result;
  if (error) throw new Error(await extractFunctionError(error, fallback));
  if (!data || data.success === false) throw new Error(data?.error || fallback);
  return data;
}
